import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { useRunReportGuard } from '@/pages/RunReport/guards/useRunReportGuard'
import { useRunReportWizardStep } from '@/pages/RunReport/hooks/useRunReportWizardStep'
import { EReportType, ERunReportStep } from '@/pages/RunReport/types'
import type { GooglePlace } from '@/pages/RunReport/utils/applyGooglePlaceToForm'
import { extractLocationParts } from '@/pages/RunReport/utils/applyGooglePlaceToForm'
import { selectRunReport } from '@/store/api/RunReport/RunReport.selector'
import {
  setCompanyInfo,
  setSocialMedia,
} from '@/store/api/RunReport/RunReport.slice'
import type { companyFormSchemaType } from '@/utils/schemas'
import { companyFormSchema } from '@/utils/schemas'

import { CompanyInfoFields } from '../../components/CompanyInfoFields'
import { InputStrengthMeter } from '../../components/InputStrengthMeter'
import { RunReportStepIndicator } from '../../components/RunReportStepIndicator'
import { SocialMediaAccountsField } from '../../components/SocialMediaAccountsField'

const FIELDS_TO_COUNT: (keyof companyFormSchemaType)[] = [
  'companyName',
  'companyWebsite',
  'industry',
  'countryLongName',
  'stateLongName',
  'cityLongName',
]

export const CompanyForm = () => {
  useRunReportGuard(ERunReportStep.CompanyForm)
  const { current, total } = useRunReportWizardStep(ERunReportStep.CompanyForm)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const slice = useAppSelector(selectRunReport)

  const form = useForm<companyFormSchemaType>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      companyName: slice.companyInfo.name,
      companyWebsite: slice.companyInfo.website,
      industry: slice.companyInfo.industry,
      description: slice.companyInfo.description ?? '',
      countryLongName: slice.companyInfo.countryLongName,
      stateLongName: slice.companyInfo.stateLongName,
      cityLongName: slice.companyInfo.cityLongName,
      socialMedia: slice.socialMedia,
    },
    mode: 'onChange',
  })

  const { handleSubmit, setValue, watch, trigger } = form

  const watchedValues = watch()
  const isFormValid = companyFormSchema.safeParse(watchedValues).success

  const [countryShortName, setCountryShortName] = useState(
    slice.companyInfo.countryShortName,
  )
  const [stateShortName, setStateShortName] = useState(
    slice.companyInfo.stateShortName,
  )
  const [cityShortName, setCityShortName] = useState(
    slice.companyInfo.cityShortName,
  )

  useEffect(() => {
    const formFields: Partial<Record<keyof companyFormSchemaType, unknown>> = {
      companyName: slice.companyInfo.name,
      companyWebsite: slice.companyInfo.website,
      industry: slice.companyInfo.industry,
      countryLongName: slice.companyInfo.countryLongName,
      stateLongName: slice.companyInfo.stateLongName,
      cityLongName: slice.companyInfo.cityLongName,
      socialMedia: slice.socialMedia,
    }
    const hydratedKeys: (keyof companyFormSchemaType)[] = []
    Object.entries(formFields).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        const typedKey = key as keyof companyFormSchemaType
        setValue(typedKey, value as never, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
        hydratedKeys.push(typedKey)
      }
    })
    if (hydratedKeys.length > 0) trigger(hydratedKeys)
  }, [slice.companyInfo, slice.socialMedia, setValue, trigger])

  // Empty values matter here: clearing/resetting a draft must clear context too.
  useEffect(() => {
    setValue('description', slice.companyInfo.description ?? '', {
      shouldValidate: true,
    })
  }, [slice.companyInfo, setValue])

  const handlePlaceSelected = (place: GooglePlace) => {
    const parts = extractLocationParts(place)
    setValue('countryLongName', parts.countryLong, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue('stateLongName', parts.stateLong, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue('cityLongName', parts.cityLong, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setCountryShortName(parts.countryShort)
    setStateShortName(parts.stateShort)
    setCityShortName(parts.cityShort)
  }

  const isAdverseMedia =
    slice.selectSearchType.reportType === EReportType.AdverseMediaReport

  const hasSocialMediaData =
    !isAdverseMedia &&
    Object.values(watch('socialMedia') ?? {}).some(
      (url) => (url ?? '').trim().length > 0,
    )

  const onSubmit = (data: companyFormSchemaType) => {
    dispatch(
      setCompanyInfo({
        name: data.companyName,
        website: data.companyWebsite,
        industry: data.industry,
        description: data.description ?? '',
        countryLongName: data.countryLongName,
        countryShortName,
        stateLongName: data.stateLongName,
        stateShortName,
        cityLongName: data.cityLongName,
        cityShortName,
      }),
    )
    dispatch(setSocialMedia(data.socialMedia))
    navigate(AppRoutes.runReportBehaviorFlags)
  }

  const handleBack = () => navigate(AppRoutes.runReportSearchForm)

  return (
    <WeScreen
      useNextButton={false}
      useSkipButton={false}
      onClickBack={handleBack}
      hideHeaderOnMobile
      contentClassName="p-0"
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex h-[calc(100dvh-3.5rem)] min-h-0 flex-col lg:h-full lg:flex-row lg:gap-6 lg:overflow-hidden lg:p-8"
        >
          <div className="hidden h-full flex-col lg:flex lg:flex-1">
            <div className="flex flex-1 flex-col justify-center gap-3 px-3">
              <RunReportStepIndicator current={current} total={total} />
              <h2 className="text-[32px] font-medium leading-tight text-neutralColor">
                {t('dashboard.runReport.companyForm.title')}
              </h2>
              <p className="text-base text-neutralColor">
                {t('dashboard.runReport.companyForm.subtitle')}
              </p>

              <div className="mt-2 flex gap-4">
                <Button type="button" variant="secondary" onClick={handleBack}>
                  {t('dashboard.runReport.companyForm.back')}
                </Button>
                <Button type="submit" disabled={!isFormValid}>
                  {t('dashboard.runReport.companyForm.continue')}
                </Button>
              </div>
            </div>

            <InputStrengthMeter
              form={form}
              fieldsToCount={FIELDS_TO_COUNT}
              hasSocialMediaData={hasSocialMediaData}
            />
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-6 lg:h-full lg:overflow-hidden lg:p-0">
            <div className="flex min-h-0 flex-1 flex-col gap-5 rounded-2xl bg-baseColorHighlight p-4 lg:h-full lg:min-h-0 lg:gap-6 lg:overflow-y-auto lg:p-6">
              <CompanyInfoFields onPlaceSelected={handlePlaceSelected} />

              {!isAdverseMedia && (
                <>
                  <div className="border-t border-neutralColor5" />
                  <SocialMediaAccountsField />
                </>
              )}
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-3 bg-baseColor px-4 pb-4 pt-3 lg:hidden">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-medium leading-tight text-neutralColor">
                {t('dashboard.runReport.companyForm.title')}
              </h2>
              <p className="text-sm text-neutralColor">
                {t('dashboard.runReport.companyForm.subtitle')}
              </p>
            </div>
            <div className="flex gap-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleBack}
                className="flex-1"
              >
                {t('dashboard.runReport.companyForm.back')}
              </Button>
              <Button type="submit" disabled={!isFormValid} className="flex-1">
                {t('dashboard.runReport.companyForm.continue')}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </WeScreen>
  )
}
